import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ListGroup, Button, Spinner } from 'react-bootstrap';
import { onSnapshot } from 'firebase/firestore';
import {
  FaEdit,
  FaTrash,
  FaChevronLeft,
  FaChevronRight,
  FaBullhorn,
} from 'react-icons/fa';
import './NoticeList.css';
import ContentUnitTag from '../common/ContentUnitTag';
import { useUser } from '../../context/UserContext';
import { useTenant } from '../../context/TenantContext';
import { useMembership } from '../../context/MembershipContext';
import { usePaginatedTenantList } from '../../hooks/usePaginatedTenantList';
import { LAST_READ_MODULES } from '../../platform/lastRead/lastReadModules';
import { useUnreadItemIds } from '../../platform/lastRead/useUnreadItemIds';
import { isPinnedActive, sortPinnedItems } from '../../platform/pinnedContent';
import PinnedHeroCard, { PinnedHeroSection } from '../../platform/PinnedHeroCard';
import ContentListFeedSection from '../../platform/ContentListFeedSection';
import { useConfirm } from '../../platform/confirm/ConfirmProvider';
import { useToast } from '../../platform/toast/ToastProvider';
import { plainTextExcerpt } from '../../platform/contentExcerpt';
import { filterByActiveTenant } from '../../services/tenantContentFilter';
import {
  canViewAllUnitsForTenant,
} from '../../services/tenantListQuery';
import { getVisibleUnitIds } from '../../services/contentQueryHelpers';
import {
  NOTICES_PAGE_SIZE,
  buildNoticesPageQuery,
  buildPinnedNoticesQuery,
  deleteNotice,
  fetchNoticesPage,
  mapNotice,
} from '../../services/noticesService';
import { fetchUnitNameMap } from '../../services/unitsService';
import {
  PREMIUM_MODULE_IDS,
  premiumListClassName,
  showListNavigateHint,
} from '../../platform/contentListVisual';
import ListItemNavigateHint from '../../platform/ListItemNavigateHint';

async function fetchNoticesPageItems(user, tenantId, memberships, options) {
  const { notices, lastVisible } = await fetchNoticesPage(user, tenantId, memberships, options);
  return { items: notices, lastVisible };
}

function NoticeList({ onSelectNotice, isAdmin, onEditNotice }) {
  const [unitNames, setUnitNames] = useState({});
  const [pinnedNotices, setPinnedNotices] = useState([]);

  const listTopRef = useRef(null);

  const { user, loading } = useUser();
  const { tenantId } = useTenant();
  const { memberships } = useMembership();

  const {
    items: notices,
    pageIndex,
    hasNextPage,
    pageLoading,
    goToPrevPage,
    goToNextPage,
    pageSize,
  } = usePaginatedTenantList({
    tenantId,
    user,
    memberships,
    userLoading: loading,
    pageSize: NOTICES_PAGE_SIZE,
    buildPageQuery: buildNoticesPageQuery,
    fetchPage: fetchNoticesPageItems,
    mapDoc: mapNotice,
    logLabel: 'notices',
  });

  useEffect(() => {
    if (loading || !user || !tenantId) {
      setPinnedNotices([]);
      return undefined;
    }

    const pinnedQuery = buildPinnedNoticesQuery(tenantId);
    const unsubscribe = onSnapshot(pinnedQuery, (querySnapshot) => {
      const items = filterByActiveTenant(
        querySnapshot.docs.map(mapNotice),
        tenantId,
      )
        .filter((notice) => isPinnedActive(notice))
        .filter((notice) => {
          if (canViewAllUnitsForTenant(user, memberships, tenantId)) {
            return true;
          }
          return getVisibleUnitIds(user).includes(notice.unit);
        });

      setPinnedNotices(sortPinnedItems(items));
    }, (error) => {
      console.error('Error fetching pinned notices:', error);
      setPinnedNotices([]);
    });

    return () => unsubscribe();
  }, [loading, user, tenantId, memberships]);

  const pinnedHeroIds = useMemo(
    () => new Set(pinnedNotices.map((item) => item.id)),
    [pinnedNotices],
  );

  const regularNotices = useMemo(
    () => notices.filter((notice) => !pinnedHeroIds.has(notice.id)),
    [notices, pinnedHeroIds],
  );

  const unreadNoticeIds = useUnreadItemIds(
    LAST_READ_MODULES.NOTICES,
    [...pinnedNotices, ...regularNotices],
  );

  const scrollListIntoView = useCallback(() => {
    listTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useEffect(() => {
    if (!tenantId) return;
    fetchUnitNameMap(tenantId).then(setUnitNames);
  }, [tenantId]);

  const confirm = useConfirm();
  const toast = useToast();

  const handleDelete = async (noticeId) => {
    const confirmed = await confirm({
      title: 'Izbriši obvestilo',
      message: 'Ali ste prepričani, da želite izbrisati to obvestilo?',
      confirmLabel: 'Izbriši',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await deleteNotice(noticeId);
      toast.deleted('Obvestilo je bilo izbrisano.');
    } catch (error) {
      console.error('Error deleting notice:', error);
      toast.error('Obvestila ni bilo mogoče izbrisati.');
    }
  };

  const handlePrevPage = () => {
    if (goToPrevPage()) {
      scrollListIntoView();
    }
  };

  const handleNextPage = () => {
    if (goToNextPage()) {
      scrollListIntoView();
    }
  };

  const formatDate = (date) =>
    date?.toLocaleString('sl-SI', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }) ?? '';

  const showPinnedSection = pageIndex === 0 && pinnedNotices.length > 0;
  const hasAnyItems = pinnedNotices.length > 0 || regularNotices.length > 0;
  const rangeStart = regularNotices.length ? pageIndex * pageSize + 1 : 0;
  const rangeEnd = pageIndex * pageSize + regularNotices.length;
  const showPagination = pageIndex > 0 || hasNextPage;
  const reserveActionsColumn = Boolean(isAdmin);
  const showNavigateHint = showListNavigateHint(PREMIUM_MODULE_IDS.NOTICES);
  const listClassName = premiumListClassName(
    PREMIUM_MODULE_IDS.NOTICES,
    'notice-list platform-content-list',
  );
  const wrapperClassName = [
    'content-list-wrapper',
    'platform-content-list-wrapper',
    showNavigateHint && 'platform-content-list-wrapper--card-stack',
  ].filter(Boolean).join(' ');

  const renderPinnedCard = (notice) => {
    const isUnread = unreadNoticeIds.includes(notice.id);

    return (
      <PinnedHeroCard
        key={notice.id}
        pinnedUntil={notice.pinnedUntil}
        thumbnailUrl={notice.thumbnailUrl}
        placeholderIcon={FaBullhorn}
        title={notice.title}
        isUnread={isUnread}
        onClick={() => onSelectNotice(notice)}
        badges={<ContentUnitTag item={notice} unitNames={unitNames} />}
        meta={(
          <>
            <span>{notice.displayName || 'Anonimen'}</span>
            <span className="benefit-list-item-separator">·</span>
            <span>{formatDate(notice.createdAt)}</span>
          </>
        )}
        actions={reserveActionsColumn ? (
          <>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onEditNotice(notice);
              }}
            >
              <FaEdit /> Uredi
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(notice.id);
              }}
            >
              <FaTrash /> Izbriši
            </Button>
          </>
        ) : undefined}
      />
    );
  };

  const renderRegularItem = (notice) => {
    const isUnread = unreadNoticeIds.includes(notice.id);
    const excerpt = plainTextExcerpt(notice.text);

    return (
      <ListGroup.Item
        key={notice.id}
        className={`notice-list-item platform-content-list-item${isUnread ? ' notice-list-item--unread platform-content-list-item--unread' : ''}`}
        onClick={() => onSelectNotice(notice)}
      >
        <div
          className={`notice-list-item-main platform-content-list-item-main${isAdmin || showNavigateHint ? ' notice-list-item-main--has-actions platform-content-list-item-main--has-actions' : ''}`}
        >
          <div className="notice-list-item-content platform-content-list-item-content">
            <div className="platform-content-list-item-thumb">
              {isUnread && (
                <span className="platform-content-list-item-unread-badge">
                  <span
                    className="platform-unread-dot"
                    title="Neprebrano"
                    aria-label="Neprebrano"
                  />
                </span>
              )}
              {notice.thumbnailUrl ? (
                <img src={notice.thumbnailUrl} alt="" loading="lazy" />
              ) : (
                <div className="platform-content-list-item-thumb-placeholder" aria-hidden="true">
                  <FaBullhorn />
                </div>
              )}
            </div>

            <div className="notice-list-item-body platform-content-list-item-body">
              <div className="notice-list-item-title platform-content-list-item-title">{notice.title}</div>
              <div className="notice-list-item-badges platform-content-list-item-badges">
                <ContentUnitTag item={notice} unitNames={unitNames} />
              </div>
              {excerpt && (
                <p className="platform-content-list-item-excerpt platform-content-list-item-excerpt--premium">
                  {excerpt}
                </p>
              )}
              <div className="notice-list-item-footer platform-content-list-item-footer">
                <span>Ustvaril {notice.displayName || 'Anonimen'}</span>
                <span className="notice-list-item-separator">·</span>
                <span>{formatDate(notice.createdAt)}</span>
              </div>
            </div>
          </div>

          {(isAdmin || showNavigateHint) && (
            <div className="notice-list-item-aside platform-content-list-item-aside">
              <div className="platform-content-list-item-aside-inner">
                {isAdmin ? (
                  <div className="notice-list-item-actions platform-content-list-item-actions">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditNotice(notice);
                      }}
                    >
                      <FaEdit /> Uredi
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(notice.id);
                      }}
                    >
                      <FaTrash /> Izbriši
                    </Button>
                  </div>
                ) : null}
                {showNavigateHint ? <ListItemNavigateHint /> : null}
              </div>
            </div>
          )}
        </div>
      </ListGroup.Item>
    );
  };

  if (loading) {
    return (
      <div className="platform-content-list-loading">
        <Spinner animation="border" role="status" size="sm">
          <span className="visually-hidden">Nalaganje...</span>
        </Spinner>
      </div>
    );
  }

  if (!pageLoading && !hasAnyItems) {
    return (
      <div className={`platform-content-list-empty${showNavigateHint ? ' platform-content-list-empty--premium' : ''}`}>
        <strong>Ni obvestil</strong>
        <span>Trenutno ni objavljenih obvestil za prikaz.</span>
      </div>
    );
  }

  return (
    <div className={wrapperClassName} ref={listTopRef}>
      {pageLoading && (
        <div className="platform-content-list-loading platform-content-list-loading--overlay">
          <Spinner animation="border" role="status" size="sm">
            <span className="visually-hidden">Nalaganje...</span>
          </Spinner>
        </div>
      )}

      {!pageLoading && showPinnedSection && (
        <PinnedHeroSection
          title="Pripeta obvestila"
          count={pinnedNotices.length}
          ariaLabel="Pripeta obvestila"
        >
          {pinnedNotices.map(renderPinnedCard)}
        </PinnedHeroSection>
      )}

      {!pageLoading && (regularNotices.length > 0 || pageIndex > 0) && (
        <ContentListFeedSection
          heading={showPinnedSection && regularNotices.length > 0 ? 'Ostala obvestila' : null}
          ariaLabel="Seznam obvestil"
        >
          <ListGroup className={listClassName}>
            {regularNotices.map(renderRegularItem)}
          </ListGroup>
        </ContentListFeedSection>
      )}

      {showPagination && (
        <nav className="platform-content-list-pagination" aria-label="Strani obvestil">
          <Button
            variant="outline-secondary"
            size="sm"
            className="platform-content-list-pagination__btn"
            onClick={handlePrevPage}
            disabled={pageIndex === 0 || pageLoading}
          >
            <FaChevronLeft aria-hidden="true" />
            <span>Prejšnja</span>
          </Button>

          <div className="platform-content-list-pagination__info">
            <span className="platform-content-list-pagination__page">Stran {pageIndex + 1}</span>
            {regularNotices.length > 0 && (
              <span className="platform-content-list-pagination__range">
                Prikazano {rangeStart}–{rangeEnd}
              </span>
            )}
          </div>

          <Button
            variant="outline-secondary"
            size="sm"
            className="platform-content-list-pagination__btn"
            onClick={handleNextPage}
            disabled={!hasNextPage || pageLoading}
          >
            <span>Naslednja</span>
            <FaChevronRight aria-hidden="true" />
          </Button>
        </nav>
      )}
    </div>
  );
}

NoticeList.defaultProps = {
  onEditNotice: () => {},
};

export default NoticeList;
